// Longest PAN length across major card networks.
export const MAX_PAN_LENGTH = 19;

const EXPIRY_DIGITS = 4;

// digitsOf strips everything but the digits from a formatted field value.
export function digitsOf(value) {
  return (value || "").replace(/\D/g, "");
}

function digitCountBefore(value, caret) {
  return digitsOf((value || "").slice(0, caret)).length;
}

// Backspace or Delete that removed only a separator removes the digit next to
// it instead; otherwise reformatting puts the separator straight back.
function deleteAcrossSeparator(digits, before, inputType, previousDigits) {
  if (digits !== previousDigits) return { digits, before };
  if (inputType === "deleteContentBackward" && before > 0) {
    return { digits: digits.slice(0, before - 1) + digits.slice(before), before: before - 1 };
  }
  if (inputType === "deleteContentForward") {
    return { digits: digits.slice(0, before) + digits.slice(before + 1), before };
  }
  return { digits, before };
}

// caretAfterDigits is the position in value just past its nth digit.
function caretAfterDigits(value, n) {
  if (n <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < value.length; i++) {
    if (/\d/.test(value[i]) && ++seen === n) return i + 1;
  }
  return value.length;
}

// formatCardNumber groups the digits in fours ("1234 5678 9012 3456") and keeps
// the caret next to the digit it was next to.
export function formatCardNumber(raw, caret, inputType, previousDigits = "") {
  let { digits, before } = deleteAcrossSeparator(
    digitsOf(raw), digitCountBefore(raw, caret), inputType, previousDigits,
  );
  digits = digits.slice(0, MAX_PAN_LENGTH);
  before = Math.min(before, digits.length);

  const value = digits.replace(/(\d{4})(?=\d)/g, "$1 ");
  return { digits, value, caret: caretAfterDigits(value, before) };
}

// formatExpiry renders MM/YY, adding the slash once the month is typed. A
// pasted or autofilled MM/YYYY keeps the last two digits of the year.
export function formatExpiry(raw, caret, inputType, previousDigits = "") {
  let all = digitsOf(raw);
  if (all.length === 6) all = all.slice(0, 2) + all.slice(4);

  let { digits, before } = deleteAcrossSeparator(
    all, digitCountBefore(raw, caret), inputType, previousDigits,
  );
  digits = digits.slice(0, EXPIRY_DIGITS);
  before = Math.min(before, digits.length);

  const deleting = (inputType || "").startsWith("delete");
  let value = digits;
  if (digits.length > 2) {
    value = `${digits.slice(0, 2)}/${digits.slice(2)}`;
  } else if (digits.length === 2 && !deleting) {
    value = `${digits}/`;
  }

  const atMonthEnd = before === 2 && digits.length === 2;
  return { digits, value, caret: atMonthEnd ? value.length : caretAfterDigits(value, before) };
}

// isCardNumberComplete reports whether the number has reached the length its
// network uses: 15 for Amex, 16 for the others, or the 19-digit maximum.
export function isCardNumberComplete(digits) {
  const expected = /^3[47]/.test(digits) ? 15 : 16;
  return digits.length === expected || digits.length === MAX_PAN_LENGTH;
}

// luhnValid mirrors pkg/pan: the mod-10 checksum every real card number
// satisfies, so a mistyped number is caught before any request is made.
export function luhnValid(digits) {
  if (!/^\d+$/.test(digits)) return false;
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let d = Number(digits[i]);
    if (double) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    double = !double;
  }
  return sum % 10 === 0;
}

// expiryStatus classifies the MMYY digits typed so far: "incomplete" until all
// four are there, "invalid" for a month outside 01-12, "expired" when the card
// ran out before the current month, otherwise "valid". A two-digit year means
// 20YY, as on the server.
export function expiryStatus(digits, now = new Date()) {
  if (digits.length < EXPIRY_DIGITS) return "incomplete";
  const month = Number(digits.slice(0, 2));
  const year = 2000 + Number(digits.slice(2, 4));
  if (month < 1 || month > 12) return "invalid";
  const currentMonths = now.getFullYear() * 12 + now.getMonth();
  if (year * 12 + month - 1 < currentMonths) return "expired";
  return "valid";
}
